import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleMinus,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import type {
  AuthorizationExpectedRoute,
  AuthorizationExpectedStep,
} from '@/services/authorization-case.service';

/**
 * Expected Route layer — Stage 5E, the **§22 #6 REPLACE** answer.
 *
 * ── WHAT THIS REPLACED ──────────────────────────────────────────────────────
 * The old `CaseProgress` helper on the case-detail page drew a **hardcoded**
 * pipeline — `Created → OPS Review → {Commercial|Admin} Review → OPS Release →
 * Engineer Resumed` — and greyed out the stages after a computed "current"
 * index. Those greyed-out stages were **predicted future state**, which
 * **D-112 OD-R1** forbids outright: *"No predicted or future stage may be
 * rendered, including greyed-out 'upcoming' nodes."* **D-113 §5** says the same
 * in terms and refuses to validate it.
 *
 * This panel replaces it with the **governed** expected route: the case's own
 * immutable published version (D-119 §4, bound by 5C) compared against its
 * actual journey (5D, derived on read). Nothing here is computed from
 * `status`/`workflowStage` combinations, and nothing is stored.
 *
 * ── THE EIGHT CONSTRAINTS (design §11.1, D-113 §5) ──────────────────────────
 *  1. Never label an expected stage "Next"          → no such label exists here
 *  2. Never imply an expected stage WILL happen     → wording is "Not reached"
 *  3. Never mix expected steps into actual history  → this is a separate panel
 *  4. Never fill an integrity gap                   → INTEGRITY_GAP renders no steps
 *  5. Never invent a missing hop                    → the panel draws no hops
 *  6. Deviation must remain visibly possible        → DEVIATED is stated plainly
 *  7. Actual history always wins                    → the journey panel is untouched
 *  8. Expected clearly distinguished from actual    → distinct heading and framing
 *
 * ── AND WHAT IT IS NOT ──────────────────────────────────────────────────────
 * **Not a source of truth.** It renders what 5D derived and computes no route
 * state of its own — there is no second expected-route model in the UI. It is
 * DESCRIPTIVE: a deviation is a description, never a fault and never a refusal
 * (D-113 §4, D-116 O-4). No persistence, no CaseProgress model, no workflow
 * legality.
 */

/** Design §11.3 — the wording rules, as data so a test can assert them. */
const OUTCOME_COPY: Record<
  AuthorizationExpectedRoute['outcome'],
  { label: string; detail: string }
> = {
  UNCONFIGURED: {
    label: 'No expected route configured',
    detail:
      'No expected route is configured for this request type. This is not a problem with the case.',
  },
  INTEGRITY_GAP: {
    label: 'Expected route not shown',
    detail:
      'The recorded journey could not be self-verified, so no expected route is shown beside it.',
  },
  ON_ROUTE: {
    label: 'On the expected route',
    detail: 'Every recorded movement so far matches the configured expected route.',
  },
  DEVIATED: {
    label: 'Deviated from the expected route',
    detail:
      'At least one recorded movement did not match the configured route. A deviation is not an error.',
  },
  COMPLETE: {
    label: 'Expected route complete',
    detail: 'A terminal step of the configured route was reached.',
  },
  IN_PROGRESS: {
    label: 'Expected route in progress',
    detail: 'No step of the configured route has been reached yet.',
  },
};

/** Design §11.3 — "NOT REACHED", never "Pending", "Remaining" or "Coming up". */
const STEP_COPY: Record<
  AuthorizationExpectedStep['classification'],
  { label: string; icon: typeof faCircleCheck; tone: string }
> = {
  REACHED: { label: 'Reached', icon: faCircleCheck, tone: 'text-green-700' },
  NOT_REACHED: { label: 'Not reached', icon: faCircleMinus, tone: 'text-muted-foreground' },
  SKIPPED_MANDATORY: {
    label: 'Mandatory step not reached',
    icon: faTriangleExclamation,
    tone: 'text-amber-700',
  },
};

interface Props {
  expected: AuthorizationExpectedRoute | undefined;
}

export function ExpectedRoutePanel({ expected }: Props) {
  // "Not sent" is not the same claim as UNCONFIGURED, so render nothing rather
  // than assert a state the server never reported.
  if (!expected) return null;

  const copy = OUTCOME_COPY[expected.outcome];
  const showSteps = expected.steps.length > 0;

  return (
    <section
      aria-labelledby="expected-route-heading"
      data-testid="expected-route-panel"
      data-outcome={expected.outcome}
      className="rounded-xl border border-dashed border-border bg-muted/30"
    >
      <div className="border-b border-border px-5 py-3.5">
        <h3 id="expected-route-heading" className="text-sm font-semibold text-foreground">
          Expected Route
        </h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {/* Constraint 8 — says outright what this layer is, and is not. */}
          What was configured for this request type — not a prediction of what will happen.
          {expected.versionNumber !== null && (
            <span data-testid="expected-route-version"> Version {expected.versionNumber}.</span>
          )}
        </p>
      </div>

      <div className="px-5 py-4">
        <p data-testid="expected-route-outcome" className="text-sm font-medium text-foreground">
          {copy.label}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">{copy.detail}</p>

        {showSteps && (
          <ol
            aria-label="Configured expected route steps"
            className="mt-4 flex flex-col gap-2"
            data-testid="expected-route-steps"
          >
            {expected.steps.map((step) => {
              const stepCopy = STEP_COPY[step.classification];
              return (
                <li
                  key={step.stepId}
                  data-testid={`expected-step-${step.sequence}`}
                  data-classification={step.classification}
                  className="flex items-center gap-2.5 rounded-lg border border-border bg-card px-3 py-2 text-sm"
                >
                  <span className="w-6 shrink-0 text-xs font-semibold text-muted-foreground">
                    {step.sequence}
                  </span>
                  {/* Decorative — the state is also carried in text below, so
                      neither colour nor icon is ever the sole indicator. */}
                  <FontAwesomeIcon
                    icon={stepCopy.icon}
                    aria-hidden="true"
                    className={`h-3.5 w-3.5 shrink-0 ${stepCopy.tone}`}
                  />
                  <span className={`text-xs font-medium ${stepCopy.tone}`}>{stepCopy.label}</span>
                  {step.mandatory && (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                      Mandatory
                    </span>
                  )}
                  {step.terminal && (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                      Completes the route
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </section>
  );
}
